/* ============================================================
   LEFTOVRZ — cart drawer + quick view

   Everything here talks to Shopify's own endpoints. Nothing keeps its own
   copy of the cart: /cart.js is the truth, every mutation re-reads it, and
   the DOM is drawn from that. A locally-tracked cart is the classic way
   these drift — a second tab, an expired session or a sold-out line and the
   drawer is confidently wrong.

     GET  /cart.js                  current cart
     POST /cart/add.js              add a variant
     POST /cart/change.js           set a line's quantity (0 removes)
     GET  /products/<handle>.js     product for the quick view

   Money is formatted from the shop's own money_format, handed in by Liquid,
   so a store in Rs. does not get dollars invented for it.
   ============================================================ */
(() => {
  const CFG = window.LEFTOVRZ_SHOP || {};
  const MONEY = CFG.moneyFormat || '${{amount}}';
  const RT = CFG.routes || {};
  const cartUrl = RT.cart || '/cart';

  /* Shopify's money_format is a template with a handful of amount tokens.
     Supporting the four real ones is less code than pulling in a library
     and avoids a whole class of "why is it 1234.00" bugs. */
  const money = (cents) => {
    const n = (cents || 0) / 100;
    const grp = (v, dec, sep = ',', dot = '.') => {
      const [i, d] = v.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (d ? dot + d : '');
    };
    return MONEY.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, tok) => {
      switch (tok) {
        case 'amount':                 return grp(n, 2);
        case 'amount_no_decimals':     return grp(n, 0);
        case 'amount_with_comma_separator':    return grp(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return grp(n, 0, '.', ',');
        default: return grp(n, 2);
      }
    });
  };

  /* Shared rather than reimplemented: the recently-viewed rail and the Call
     it slider both need the shop's own money format, and a second copy of
     this would be a second thing to get wrong. */
  window.__lvMoney = money;

  const esc = (t) => String(t == null ? '' : t)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /* ---------- overlay plumbing, shared by both surfaces ----------
     Focus is moved in and restored on close, the page behind is locked,
     and Escape closes — the three things a hand-rolled modal usually
     forgets and a keyboard user immediately notices. */
  let openOv = null, lastFocus = null;
  const openOverlay = (el) => {
    if (openOv) closeOverlay();
    lastFocus = document.activeElement;
    openOv = el;
    el.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
    const first = el.querySelector('[data-autofocus],button,a[href],input');
    if (first) first.focus({ preventScroll: true });
  };
  const closeOverlay = () => {
    if (!openOv) return;
    openOv.dataset.open = 'false';
    openOv = null;
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.isConnected) lastFocus.focus({ preventScroll: true });
  };
  addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openOv) { e.preventDefault(); closeOverlay(); }
    // keep tabbing inside the open overlay
    if (e.key === 'Tab' && openOv) {
      const f = [...openOv.querySelectorAll(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])')]
        .filter((n) => n.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-ov-close]')) { e.preventDefault(); closeOverlay(); }
  });

  /* ---------- cart ---------- */
  const drawer = document.getElementById('cart-drawer');
  const linesEl = drawer && drawer.querySelector('[data-cart-lines]');
  const sumEl  = drawer && drawer.querySelector('[data-cart-subtotal]');
  const footEl = drawer && drawer.querySelector('[data-cart-foot]');

  const setCount = (n) => {
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = n;
      el.hidden = !n;
    }
  };

  const drawCart = (cart) => {
    setCount(cart.item_count);
    if (!linesEl) return;
    if (!cart.items.length) {
      linesEl.innerHTML =
        '<p class="cempty mono">Nothing in the bag yet</p>';
      if (footEl) footEl.hidden = true;
      return;
    }
    if (footEl) footEl.hidden = false;
    linesEl.innerHTML = cart.items.map((it) => `
      <div class="cline" data-key="${esc(it.key)}">
        <a class="cline__media" href="${esc(it.url)}">
          ${it.image ? `<img src="${esc(it.image)}" alt="" loading="lazy">` : ''}
        </a>
        <div>
          <h3 class="cline__name">${esc(it.product_title)}</h3>
          ${it.variant_title && it.variant_title !== 'Default Title'
            ? `<p class="cline__opt mono muted">${esc(it.variant_title)}</p>` : ''}
          <div class="cline__qty">
            <button type="button" data-step="-1" aria-label="Reduce quantity">&minus;</button>
            <output aria-label="Quantity">${it.quantity}</output>
            <button type="button" data-step="1" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <span class="cline__price">${money(it.final_line_price)}</span>
      </div>`).join('');
    if (sumEl) sumEl.textContent = money(cart.total_price);
  };

  const getCart = () =>
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json());

  const refresh = () => getCart().then(drawCart).catch(() => {});

  const addToCart = (id, quantity = 1) =>
    fetch(`${RT.cartAdd || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }] }),
    }).then(async (r) => {
      const data = await r.json();
      // Shopify answers a rejected add with 422 and a human message —
      // surfacing it beats a silent no-op the shopper cannot explain.
      if (!r.ok) throw new Error(data.description || data.message || 'Could not add');
      return data;
    });

  const changeLine = (key, quantity) =>
    fetch(`${RT.cartChange || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());

  // quantity steppers, delegated so re-rendered lines keep working
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-step]');
    if (!step || !linesEl || !linesEl.contains(step)) return;
    const line = step.closest('.cline');
    const out = line.querySelector('output');
    const next = Math.max(0, (+out.textContent || 0) + (+step.dataset.step));
    step.closest('.cline__qty').querySelectorAll('button').forEach((b) => (b.disabled = true));
    changeLine(line.dataset.key, next).then(drawCart).catch(refresh);
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      if (drawer) { openOverlay(drawer); refresh(); }
    }
    /* The size guide opens over the page. Delegated like everything else
       here, so it keeps working after a Barba swap without re-binding. */
    if (e.target.closest('[data-size-open]')) {
      e.preventDefault();
      const sg = document.getElementById('size-guide');
      if (sg) openOverlay(sg);
    }
  });

  /* Add-to-cart from anywhere: product page or quick view. The button
     reports its own state so the shopper is never left wondering whether
     the click registered. */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-add-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const id = +form.querySelector('[name="id"]').value;
    const qtyEl = form.querySelector('[name="quantity"]');
    const qty = Math.max(1, +(qtyEl && qtyEl.value) || 1);
    if (!id) return;
    const label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    addToCart(id, qty)
      .then(() => { if (drawer) openOverlay(drawer); return refresh(); })
      .catch((err) => { if (btn) btn.textContent = err.message || 'Unavailable'; })
      .finally(() => {
        setTimeout(() => { if (btn) { btn.disabled = false; btn.textContent = label; } }, 900);
      });
  });

  /* A discount is only a discount when the compare-at is ABOVE the price.
     Shopify will happily store one that is equal or lower, and printing
     "-0%" on those is worse than printing nothing. */
  const showCompare = (scope, price, compare) => {
    const was = scope.querySelector('[data-compare]');
    const off = scope.querySelector('[data-off]');
    const on = Number(compare) > Number(price);
    if (was) { was.textContent = on ? money(+compare) : ''; was.hidden = !on; }
    if (off) {
      off.textContent = on
        ? '\u2212' + Math.round((compare - price) / compare * 100) + '%'
        : '';
      off.hidden = !on;
    }
  };

  /* ---------- variant picking (product page + quick view) ---------- */
  const wireVariants = (scope) => {
    const input = scope.querySelector('[name="id"]');
    const btn = scope.querySelector('[data-add-btn]');
    const priceEl = scope.querySelector('[data-price]');
    if (!input) return;
    scope.addEventListener('click', (e) => {
      const b = e.target.closest('[data-variant]');
      if (!b || b.disabled) return;
      for (const o of scope.querySelectorAll('[data-variant]')) {
        o.setAttribute('aria-pressed', String(o === b));
      }
      input.value = b.dataset.variant;
      if (priceEl && b.dataset.price) priceEl.textContent = money(+b.dataset.price);
      showCompare(scope, +b.dataset.price, +(b.dataset.compare || 0));
      /* The grade chip follows the variant. Sizes are not all in the same
         condition on a resale store, so a chip that keeps showing the first
         variant's grade is actively misleading. */
      const chip = document.querySelector('[data-pdp-cond]');
      if (chip && b.dataset.cond) {
        chip.textContent = b.dataset.cond;
        if (b.dataset.grade) chip.dataset.grade = b.dataset.grade;
      }
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || 'Add to bag'; }
    });
  };
  for (const scope of document.querySelectorAll('[data-add-form]')) wireVariants(scope);

  /* ---------- quick view ----------
     Fetches the product as JSON rather than duplicating its markup into
     every card: the grid stays light, and the sheet always shows what the
     product actually is right now. */
  const qv = document.getElementById('quick-view');
  const qvBody = qv && qv.querySelector('[data-qv-body]');

  document.addEventListener('click', (e) => {
    const peek = e.target.closest('[data-quick-view]');
    if (!peek || !qv) return;
    e.preventDefault();
    const handle = peek.dataset.quickView;
    qvBody.innerHTML = '<p class="mono muted">Loading…</p>';
    openOverlay(qv);
    fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => { if (!r.ok) throw new Error('404'); return r.json(); })
      .then((p) => {
        const img = p.featured_image || (p.images && p.images[0]);
        const first = p.variants.find((v) => v.available) || p.variants[0];
        const single = p.variants.length === 1
          && p.variants[0].title === 'Default Title';

        /* This catalogue sells on "Shoe size" x "CONDITION", so a variant
           title is "42 / GOOD". Printed whole on a size button it reads as
           a size called "42 / GOOD"; the grade belongs on its own badge,
           the way the card shows it. Older payloads hand back option names
           as plain strings, hence the shape check. */
        const optNames = (p.options || []).map((o) => (typeof o === 'string' ? o : o && o.name) || '');
        const idxOf = (re) => optNames.findIndex((n) => re.test(n));
        const sizeIdx = Math.max(0, idxOf(/size/i));
        const condIdx = idxOf(/condit/i);
        const optOf = (v, i) => (i >= 0 && v.options && v.options[i]) || '';
        const sizeLabel = (v) => optOf(v, sizeIdx) || v.title;
        const cond = first ? optOf(first, condIdx) : '';
        qvBody.innerHTML = `
          <div class="qv">
            <div class="qv__media">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}">` : ''}</div>
            <form data-add-form>
              <p class="mono muted">${esc(p.vendor || '')}</p>
              <h2 class="qv__title">${esc(p.title)}</h2>
              ${cond ? `<p class="qv__cond">${esc(cond)}</p>` : ''}
              <p class="qv__price">
                <span data-price>${money(first ? first.price : p.price)}</span>
                <s class="qv__was" data-compare hidden></s>
                <span class="qv__off" data-off hidden></span>
              </p>
              ${single ? '' : `
                <p class="mono muted">Size</p>
                <div class="pdp__row">
                  ${p.variants.map((v) => `
                    <button class="pdp__size" type="button" data-variant="${v.id}"
                      data-price="${v.price}" data-compare="${v.compare_at_price || 0}"
                      ${v.available ? '' : 'disabled'}
                      aria-pressed="${first && v.id === first.id}">${esc(sizeLabel(v))}</button>`).join('')}
                </div>`}
              <input type="hidden" name="id" value="${first ? first.id : ''}">
              <button class="btn pdp__add" type="submit" data-add-btn
                      data-label="Add to bag" ${first && first.available ? '' : 'disabled'}>
                ${first && first.available ? 'Add to bag' : 'Sold out'}
              </button>
              <a class="qv__full" href="${esc(p.url)}">Full details &rarr;</a>
            </form>
          </div>`;
        const form = qvBody.querySelector('[data-add-form]');
        wireVariants(form);
        if (first) showCompare(form, first.price, first.compare_at_price || 0);
      })
      .catch(() => {
        qvBody.innerHTML =
          `<p class="mono muted">Could not load this one. <a href="/products/${esc(handle)}">Open the product page</a>.</p>`;
      });
  });

  refresh();   // the header count must be right on a cold load too

  /* ============================================================
     Buy it now, and the follow-along buy bar
     ============================================================ */

  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)');

  /* One burst of the intro's channel split. The class is removed first and
     the element read back in between, because re-adding a class the element
     already carries does not restart a CSS animation. */
  const tempt = (el) => {
    if (!el || el.disabled || REDUCE.matches) return;
    el.classList.remove('is-tempt');
    void el.offsetWidth;
    el.classList.add('is-tempt');
    setTimeout(() => el.classList.remove('is-tempt'), 1600);
  };

  /* Straight to checkout. This adds and then leaves, rather than using
     Shopify's dynamic checkout button, because that button renders Shopify's
     own chrome and cannot be brought into this theme's language. The
     trade-off is no Shop Pay / Google Pay express buttons. */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-buy-now]');
    if (!b) return;
    const form = b.closest('[data-add-form]');
    if (!form) return;
    const idEl = form.querySelector('[name="id"]');
    const qtyEl = form.querySelector('[name="quantity"]');
    const id = +(idEl && idEl.value);
    if (!id) return;
    const label = b.textContent;
    b.disabled = true;
    b.textContent = 'One moment…';
    addToCart(id, Math.max(1, +(qtyEl && qtyEl.value) || 1))
      .then(() => { window.location.assign('/checkout'); })
      .catch((err) => {
        b.disabled = false;
        b.textContent = err.message || 'Unavailable';
        setTimeout(() => { b.textContent = label; }, 1600);
      });
  });

  const wireBuyBar = () => {
    const bar = document.querySelector('[data-buybar]');
    const form = document.querySelector('[data-add-form]');
    const anchor = document.querySelector('.pdp__cta');
    if (!bar || !form || !anchor || bar.dataset.wired) return;
    bar.dataset.wired = '1';

    const addBtn = bar.querySelector('[data-bb-add]');
    const out = bar.querySelector('[data-bb-qty]');
    const idField = form.querySelector('[name="id"]');
    const qtyField = form.querySelector('[name="quantity"]');
    const mainBtn = form.querySelector('[data-add-btn]');
    const every = Math.max(0, +bar.dataset.temptEvery || 0) * 1000;

    let done = false;   // this product is in the bag; the bar has no job left
    let qty = 1;

    /* ---- show / hide ---------------------------------------------------
       Only once the real button has gone off the TOP of the screen. Plain
       !isIntersecting is also true before you have reached it, which would
       show the bar to someone who has not seen the product yet. */
    /* A fixed bar has to give back the room it takes, or whatever is at the
       real bottom of the page sits underneath it — on a phone that is the
       social rail, which now lives in the page rather than being pinned.
       Measured rather than assumed, because the bar is one row or two
       depending on the width and on which controls the product even has. */
    const room = (on) => {
      document.body.style.paddingBottom = on ? bar.offsetHeight + 'px' : '';
    };

    const show = (on) => {
      if (on && !done) {
        bar.hidden = false;
        bar.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => { bar.classList.add('is-up'); room(true); });
      } else {
        bar.classList.remove('is-up');
        bar.setAttribute('aria-hidden', 'true');
        room(false);
        setTimeout(() => { if (!bar.classList.contains('is-up')) bar.hidden = true; }, 420);
      }
    };
    let rt;
    addEventListener('resize', () => {
      clearTimeout(rt);
      rt = setTimeout(() => { if (bar.classList.contains('is-up')) room(true); }, 140);
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([e]) => show(!e.isIntersecting && e.boundingClientRect.top < 0),
        { threshold: 0 }
      ).observe(anchor);
    }

    /* The size select and the grade chip are driven by wirePicker, which is
       the one place that resolves a variant — there is nothing to wire here.
       What is left is quantity and the add button. */

    bar.addEventListener('click', (e) => {
      const step = e.target.closest('[data-bb-step]');
      if (!step) return;
      qty = Math.min(99, Math.max(1, qty + (+step.dataset.bbStep)));
      out.textContent = qty;
      if (qtyField) qtyField.value = qty;
    });

    if (addBtn) addBtn.addEventListener('click', () => {
      const id = +(idField && idField.value);
      if (!id) return;
      const label = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      addToCart(id, qty)
        .then(() => {
          done = true;                  // job done: stand down for good
          show(false);
          if (drawer) openOverlay(drawer);
          return refresh();
        })
        .catch((err) => { addBtn.textContent = err.message || 'Unavailable'; })
        .finally(() => setTimeout(() => {
          addBtn.disabled = false;
          addBtn.textContent = label;
        }, 900));
    });

    /* adding from the page itself also retires the bar */
    if (mainBtn) mainBtn.addEventListener('click', () => { done = true; show(false); });

    /* ---- the nudge -----------------------------------------------------
       Whichever button the shopper can actually see is the one that moves.
       It stops for good once the product is in the bag, and it never starts
       under prefers-reduced-motion. */
    if (every > 0 && !REDUCE.matches) {
      const timer = setInterval(() => {
        if (done || document.hidden) return;
        if (!document.body.contains(bar)) { clearInterval(timer); return; }
        tempt(bar.classList.contains('is-up') ? addBtn : mainBtn);
      }, every);
    }
  };

  window.__lvBuyBar = wireBuyBar;
  wireBuyBar();


  /* ============================================================
     Option picker — size and condition as two questions
     ============================================================ */
  const wirePicker = () => {
    const picker = document.querySelector('[data-picker]');
    const mapTag = document.querySelector('[data-variant-map]');
    const form = document.querySelector('[data-add-form]');
    if (!picker || !mapTag || !form || picker.dataset.wired) return;

    let variants = [];
    try { variants = JSON.parse(mapTag.textContent); } catch (e) { return; }
    if (!variants.length) return;
    picker.dataset.wired = '1';

    const idField = form.querySelector('[name="id"]');
    const addBtn = form.querySelector('[data-add-btn]');
    const buyBtn = form.querySelector('[data-buy-now]');
    const priceEl = form.querySelector('[data-price]');
    const chip = document.querySelector('[data-pdp-cond]');
    const condRow = picker.querySelector('.pdp__size--cond');
    const condIdx = condRow ? +condRow.dataset.opt : -1;
    const bbSize = document.querySelector('[data-bb-size]');
    const bbCond = document.querySelector('[data-bb-cond]');
    const bbPrice = document.querySelector('[data-bb-price]');
    const bbWas = document.querySelector('[data-bb-was]');
    const bbOff = document.querySelector('[data-bb-off]');
    const bbAdd = document.querySelector('[data-bb-add]');

    /* what is currently chosen, one entry per option */
    const chosen = [];
    for (const b of picker.querySelectorAll('[aria-pressed="true"]')) {
      chosen[+b.dataset.opt] = b.dataset.val;
    }

    const match = (want) =>
      variants.find((v) => want.every((c, i) => c == null || v.opts[i] === c));

    /* Options the shopper never picked — they came with the pair. */
    const derived = new Set();

    /* A value that leads to no variant at all is disabled; one that leads to
       a variant that is out of stock stays clickable but is marked, because
       "there is no such pair" and "we sold that pair" are different answers
       and a shopper deserves to be told which one they hit. */
    const marks = () => {
      for (const b of picker.querySelectorAll('[data-opt]')) {
        const i = +b.dataset.opt;
        const probe = chosen.slice();
        probe[i] = b.dataset.val;
        /* A DERIVED option must not constrain the one being probed. The
           grade was not chosen by the shopper, it came with the size — so
           testing "is a 43 available" against the 42's grade answers no for
           every size but the one already selected, and the picker locks up
           with only its current choice clickable. */
        for (const d of derived) { if (d !== i) probe[d] = null; }
        const hit = match(probe);
        b.disabled = !hit;
        b.classList.toggle('is-gone', !!hit && !hit.available);
      }
    };

    /* These products are one of a kind: one size, one grade, one pair. An
       option with a single value across the whole product is not a question,
       and a picker offering one answer is furniture rather than a control.

       Whether an option is a singleton is a fact about the PRODUCT, so it is
       settled once here instead of being re-derived from whatever happens to
       be selected. An earlier version probed it contextually and got it
       wrong: the probe filtered by the other option's current value, which
       on first paint was a value no variant actually had, and an option that
       matched nothing does not look the same as an option that matches one
       thing. Counting the distinct values outright cannot go wrong that way. */
    const optCount = variants[0] ? variants[0].opts.length : 0;
    const rowOf = (i) => {
      const b = picker.querySelector(`[data-opt="${i}"]`);
      return b ? b.closest('.pdp__row') : null;
    };
    const valuesFor = (i) => [...new Set(variants.map((v) => v.opts[i]))];

    for (let i = 0; i < optCount; i++) {
      const vals = valuesFor(i);
      if (vals.length > 1) continue;
      chosen[i] = vals[0];
      derived.add(i);
      const group = rowOf(i);
      if (group) {
        group.hidden = true;
        if (group.previousElementSibling) group.previousElementSibling.hidden = true;
        for (const o of picker.querySelectorAll(`[data-opt="${i}"]`)) {
          o.setAttribute('aria-pressed', String(o.dataset.val === chosen[i]));
        }
      }
      if (bbSize && +bbSize.dataset.opt === i) bbSize.hidden = true;
    }

    /* One variant means one pair. A quantity stepper on a one-of-a-kind item
       offers a number the shop can never fulfil, so it goes with the pickers
       rather than sitting there implying you could take two. */
    if (variants.length === 1) {
      for (const q of document.querySelectorAll('.buybar__qty')) q.hidden = true;
    }

    /* What is left is the option that DOES vary, and the grade that comes
       with whatever the shopper picks there. */
    const collapseSingles = () => {
      if (condIdx < 0 || derived.has(condIdx)) return;
      const group = rowOf(condIdx);
      if (!group) return;
      const label = group.previousElementSibling;

      const probe = chosen.slice();
      probe[condIdx] = null;
      for (const d of derived) { if (d !== condIdx) probe[d] = null; }
      const grades = [...new Set(
        variants.filter((v) => probe.every((c, k) => c == null || v.opts[k] === c))
                .map((v) => v.opts[condIdx]))];

      if (grades.length <= 1) {
        if (grades.length === 1) chosen[condIdx] = grades[0];
        derived.add(condIdx);
        group.hidden = true;
        if (label) label.hidden = true;
      } else {
        derived.delete(condIdx);
        group.hidden = false;
        if (label) label.hidden = false;
        if (!grades.includes(chosen[condIdx])) chosen[condIdx] = grades[0];
      }
      for (const o of picker.querySelectorAll(`[data-opt="${condIdx}"]`)) {
        o.setAttribute('aria-pressed', String(o.dataset.val === chosen[condIdx]));
      }
    };

    const apply = () => {
      collapseSingles();
      const v = match(chosen);
      marks();
      if (!v) return;
      if (idField) idField.value = v.id;
      if (priceEl) priceEl.textContent = money(v.price);
      showCompare(form, v.price, v.compare);
      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? (addBtn.dataset.label || 'Add to bag') : 'Sold out';
      }
      if (buyBtn) buyBtn.disabled = !v.available;
      if (chip && condIdx >= 0 && chosen[condIdx]) {
        chip.textContent = chosen[condIdx];
        const src = picker.querySelector(
          `[data-opt="${condIdx}"][data-val="${CSS.escape(chosen[condIdx])}"]`);
        if (src && src.dataset.grade) chip.dataset.grade = src.dataset.grade;
      }
      /* Keep the follow-along bar showing the same pair — the same size, the
         same grade, the same price. Two controls disagreeing about what is
         in the bag is worse than having only one of them. */
      if (bbSize && bbSize.value !== chosen[+bbSize.dataset.opt]) {
        bbSize.value = chosen[+bbSize.dataset.opt];
      }
      if (bbCond && condIdx >= 0 && chosen[condIdx]) {
        bbCond.textContent = chosen[condIdx];
        const src = picker.querySelector(
          `[data-opt="${condIdx}"][data-val="${CSS.escape(chosen[condIdx])}"]`);
        if (src && src.dataset.grade) bbCond.dataset.grade = src.dataset.grade;
      }
      if (bbPrice) bbPrice.textContent = money(v.price);
      const onSale = Number(v.compare) > Number(v.price);
      if (bbWas) { bbWas.textContent = onSale ? money(v.compare) : ''; bbWas.hidden = !onSale; }
      if (bbOff) {
        bbOff.textContent = onSale
          ? '\u2212' + Math.round((v.compare - v.price) * 100 / v.compare) + '%' : '';
        bbOff.hidden = !onSale;
      }
      if (bbAdd) {
        bbAdd.disabled = !v.available;
        bbAdd.textContent = v.available ? 'Add to cart' : 'Sold out';
      }
      document.dispatchEvent(new CustomEvent('lv:variant', { detail: v }));
    };

    picker.addEventListener('click', (e) => {
      const b = e.target.closest('[data-opt]');
      if (!b || b.disabled) return;
      const i = +b.dataset.opt;
      chosen[i] = b.dataset.val;
      for (const o of picker.querySelectorAll(`[data-opt="${i}"]`)) {
        o.setAttribute('aria-pressed', String(o === b));
      }
      apply();
    });

    /* The bar's select is the other way in to the same choice. It carries a
       SIZE now, not a variant id, so both controls are answering the same
       question and there is only one place that resolves it. */
    if (bbSize) bbSize.addEventListener('change', () => {
      const i = +bbSize.dataset.opt;
      chosen[i] = bbSize.value;
      for (const o of picker.querySelectorAll(`[data-opt="${i}"]`)) {
        o.setAttribute('aria-pressed', String(o.dataset.val === bbSize.value));
      }
      apply();
    });

    apply();
  };

  window.__lvPicker = wirePicker;
  wirePicker();

})();
